import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import apiClient, { getTrialStatus } from '../api/client';

const WRITING_PROMPTS = [
  {
    id: 1,
    title: "A Mysterious Discovery",
    prompt: "You find a strange object in your backyard that doesn't belong to this world. Describe what happens next.",
    category: "Creative Writing"
  },
  {
    id: 2,
    title: "The Perfect Day",
    prompt: "Describe your perfect day from start to finish. What would you do, where would you go, and who would you spend it with?",
    category: "Descriptive Writing"
  },
  {
    id: 3,
    title: "An Important Lesson",
    prompt: "Write about a time when you learned an important lesson. What happened and what did you learn?",
    category: "Narrative Writing"
  },
  {
    id: 4,
    title: "If I Could Change One Thing",
    prompt: "If you could change one thing about the world, what would it be and why? How would this change make things better?",
    category: "Persuasive Writing"
  },
  {
    id: 5,
    title: "A Letter to My Future Self",
    prompt: "Write a letter to yourself 10 years from now. What do you want to tell your future self?",
    category: "Personal Writing"
  }
];

function WritingStudio() {
  const navigate = useNavigate();
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [writingText, setWritingText] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [myWritings, setMyWritings] = useState([]);
  const [loadingWritings, setLoadingWritings] = useState(true);
  const [showPrompts, setShowPrompts] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [trialStatus, setTrialStatus] = useState({
    trialWritingRemaining: 0,
    hasTrialAccess: false
  });
  const [checkingPremium, setCheckingPremium] = useState(true);
  const [checkingTrial, setCheckingTrial] = useState(true);

  // Check premium status on mount
  useEffect(() => {
    checkPremiumStatus();
    fetchTrialStatus();
  }, []);

  const checkPremiumStatus = async () => {
    try {
      const attributes = await fetchUserAttributes();
      const premiumStatus = attributes['custom:premium_status'] === 'true';
      setIsPremium(premiumStatus);
    } catch (err) {
      console.error('Error checking premium status:', err);
      setIsPremium(false);
    } finally {
      setCheckingPremium(false);
    }
  };

  const fetchTrialStatus = async () => {
    try {
      const status = await getTrialStatus();
      setTrialStatus(status);
    } catch (err) {
      console.error('Error fetching trial status:', err);
    } finally {
      setCheckingTrial(false);
    }
  };

  useEffect(() => {
    loadMyWritings();
  }, []);

  useEffect(() => {
    const words = writingText.trim().split(/\s+/).filter(word => word.length > 0);
    setWordCount(words.length);
  }, [writingText]);

  const loadMyWritings = async () => {
    try {
      const response = await apiClient.get('/writings');
      setMyWritings(response.data.writings || []);
    } catch (error) {
      console.error('Error loading writings:', error);
    } finally {
      setLoadingWritings(false);
    }
  };

  const handleGetFeedback = async () => {
    if (!writingText.trim()) {
      alert('Please write something first!');
      return;
    }

    if (wordCount < 50) {
      alert('Please write at least 50 words to get meaningful feedback.');
      return;
    }

    setLoading(true);
    setFeedback(null);

    try {
      const response = await apiClient.post('/writing-feedback', {
        prompt: selectedPrompt?.prompt || 'Free writing',
        writingText: writingText
      });

      setFeedback(response.data.feedback);
    } catch (error) {
      console.error('Error getting feedback:', error);
      alert('Failed to get feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWriting = async () => {
    if (!writingText.trim()) {
      alert('Please write something first!');
      return;
    }

    setSaving(true);

    try {
      await apiClient.post('/writings', {
        prompt: selectedPrompt?.prompt || 'Free writing',
        promptTitle: selectedPrompt?.title || 'Untitled',
        writingText: writingText,
        feedback: feedback
      });

      alert('Writing saved successfully!');
      loadMyWritings();
    } catch (error) {
      console.error('Error saving writing:', error);
      alert('Failed to save writing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLoadWriting = (writing) => {
    setSelectedPrompt({
      title: writing.promptTitle,
      prompt: writing.prompt
    });
    setWritingText(writing.writingText);
    setFeedback(writing.feedback);
    setShowPrompts(false);
  };

  const handleNewWriting = () => {
    setSelectedPrompt(null);
    setWritingText('');
    setFeedback(null);
    setShowPrompts(true);
  };

  // Show loading while checking premium status and trial
  if (checkingPremium || checkingTrial) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}>⏳</div>
        <p style={{ fontSize: '18px', color: '#666' }}>Loading...</p>
      </div>
    );
  }

  // Show premium required page if not premium (Writing Studio is premium-only)
  if (!isPremium) {
    return (
      <div style={{ maxWidth: '800px', margin: '60px auto', padding: '20px' }}>
        <div className="card" style={{ 
          textAlign: 'center', 
          padding: '60px 40px',
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white'
        }}>
          <div style={{ fontSize: '80px', marginBottom: '20px' }}>🔒</div>
          <h1 style={{ fontSize: '36px', marginBottom: '20px' }}>Premium Access Required</h1>
          <p style={{ fontSize: '18px', marginBottom: '30px', opacity: '0.95', lineHeight: '1.7' }}>
            Writing Studio with expert feedback is available exclusively to premium members. 
            Upgrade your account to get instant expert feedback on your writing.
          </p>
          <div style={{ 
            background: 'rgba(255,255,255,0.2)', 
            padding: '25px', 
            borderRadius: '12px',
            marginBottom: '30px'
          }}>
            <h3 style={{ fontSize: '20px', marginBottom: '15px' }}>Premium Benefits:</h3>
            <ul style={{ 
              textAlign: 'left', 
              fontSize: '16px', 
              lineHeight: '2',
              listStyle: 'none',
              padding: 0
            }}>
              <li>✅ Unlimited writing practice with expert feedback</li>
              <li>✅ Detailed analysis of grammar, structure, and style</li>
              <li>✅ Save and track your writing progress</li>
              <li>✅ Access to all topic-based practice tests</li>
            </ul>
          </div>
          <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate('/pricing')}
              className="btn-primary"
              style={{ 
                background: 'white',
                color: '#00f2fe',
                padding: '15px 40px',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              View Pricing
            </button>
            <button 
              onClick={() => navigate('/exams')}
              className="btn-secondary"
              style={{ 
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '2px solid white',
                padding: '15px 40px',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              Try Free Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: '40px 20px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ marginBottom: '30px' }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'none',
              border: 'none',
              color: '#4f46e5',
              cursor: 'pointer',
              fontSize: '16px',
              marginBottom: '20px'
            }}
          >
            ← Back to Dashboard
          </button>
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 10px 0' }}>
            ✍️ Writing Practice Studio
          </h1>
          <p style={{ fontSize: '18px', color: '#6b7280', margin: 0 }}>
            Practice your writing skills with expert feedback. No timer, no pressure!
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: showPrompts ? '300px 1fr' : '1fr', gap: '20px' }}>
          {/* Sidebar - Prompts & My Writings */}
          {showPrompts && (
            <div>
              {/* Writing Prompts */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px' }}>📝 Writing Prompts</h3>
                {WRITING_PROMPTS.map(prompt => (
                  <div
                    key={prompt.id}
                    onClick={() => {
                      setSelectedPrompt(prompt);
                      setWritingText('');
                      setFeedback(null);
                    }}
                    style={{
                      padding: '12px',
                      marginBottom: '10px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      background: selectedPrompt?.id === prompt.id ? '#eef2ff' : '#f9fafb',
                      border: selectedPrompt?.id === prompt.id ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937', marginBottom: '4px' }}>
                      {prompt.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>
                      {prompt.category}
                    </div>
                  </div>
                ))}
              </div>

              {/* My Writings */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px' }}>📚 My Writings</h3>
                {loadingWritings ? (
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</p>
                ) : myWritings.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>No saved writings yet</p>
                ) : (
                  myWritings.slice(0, 5).map(writing => (
                    <div
                      key={writing.writingId}
                      onClick={() => handleLoadWriting(writing)}
                      style={{
                        padding: '12px',
                        marginBottom: '10px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: '#f9fafb',
                        border: '1px solid #e5e7eb',
                        transition: 'all 0.2s'
                      }}
                    >
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#1f2937', marginBottom: '4px' }}>
                        {writing.promptTitle}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {new Date(writing.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Main Writing Area */}
          <div>
            {/* Selected Prompt */}
            {selectedPrompt && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div>
                    <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1f2937', margin: '0 0 10px 0' }}>
                      {selectedPrompt.title}
                    </h3>
                    <p style={{ fontSize: '16px', color: '#4b5563', margin: 0 }}>
                      {selectedPrompt.prompt}
                    </p>
                  </div>
                  <button
                    onClick={handleNewWriting}
                    style={{
                      padding: '8px 16px',
                      background: '#f3f4f6',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    New Writing
                  </button>
                </div>
              </div>
            )}

            {/* Writing Editor */}
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>Your Writing</h3>
                <div style={{ fontSize: '14px', color: '#6b7280' }}>
                  {wordCount} words
                </div>
              </div>
              <textarea
                value={writingText}
                onChange={(e) => setWritingText(e.target.value)}
                placeholder={selectedPrompt ? "Start writing your response here..." : "Choose a prompt or start free writing..."}
                style={{
                  width: '100%',
                  minHeight: '400px',
                  padding: '15px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '16px',
                  lineHeight: '1.8',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button
                  onClick={handleGetFeedback}
                  disabled={loading || wordCount < 50}
                  style={{
                    padding: '12px 24px',
                    background: loading ? '#9ca3af' : '#4f46e5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  {loading ? '✨ Analyzing...' : '✨ Get Expert Feedback'}
                </button>
                <button
                  onClick={handleSaveWriting}
                  disabled={saving || !writingText.trim()}
                  style={{
                    padding: '12px 24px',
                    background: saving ? '#9ca3af' : '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: saving ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  {saving ? '💾 Saving...' : '💾 Save Writing'}
                </button>
              </div>
            </div>

            {/* Expert Feedback */}
            {feedback && (
              <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '15px', color: '#10b981' }}>
                  ✨ Expert Feedback
                </h3>
                <div style={{ fontSize: '16px', lineHeight: '1.8', color: '#374151', whiteSpace: 'pre-wrap' }}>
                  {feedback}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default WritingStudio;
